// src/pages/landing/PageEditorPage.tsx
// /landing/:pageId — page editor. Tabs: Content (sections, live preview),
// Settings (title/SEO/theme/access), Analytics. Header: publish, archive,
// duplicate, delete, save as template, preview link. GetLandingPage,
// UpdateLandingPage, UpdateSections, PublishLandingPage, ArchiveLandingPage,
// DuplicateLandingPage, DeleteLandingPage, GetLandingAnalytics (Analytics
// tab), CreateLandingTemplate (save-as-template dialog).

import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useI18n } from '@react/hooks';
import { Button, Badge, Alert } from '@react/components';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { landingApi } from '@core/api';
import type { LandingPage, LandingSection } from '@core/types/landing';
import { useScope } from '@/hooks/useScope';
import {
  PageAnalyticsPanel,
  PageSettingsForm,
  SaveAsTemplateDialog,
  SectionEditorList,
  SectionRenderer,
  errMessage,
  statusBadgeVariant,
} from '@/features/landing';

type Tab = 'content' | 'settings' | 'analytics';
const TABS: Tab[] = ['content', 'settings', 'analytics'];

export function PageEditorPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const scope = useScope();
  const { pageId } = useParams<{ pageId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const tab = useMemo<Tab>(() => {
    const v = searchParams.get('tab');
    return (TABS as string[]).includes(v ?? '') ? (v as Tab) : 'content';
  }, [searchParams]);

  const [page, setPage] = useState<LandingPage | null>(null);
  const [sections, setSections] = useState<LandingSection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [savingSections, setSavingSections] = useState(false);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const load = () => {
    if (!pageId) return;
    setLoading(true);
    setError(null);
    landingApi
      .GetLandingPage(pageId)
      .then((res) => {
        setPage(res.page ?? null);
        setSections(res.page?.sections ?? []);
      })
      .catch((e: unknown) => setError(errMessage(e, t('landing.pages.loadError'))))
      .finally(() => setLoading(false));
  };

  useEffect(load, [pageId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveSections = async () => {
    if (!pageId) return;
    setSavingSections(true);
    setError(null);
    try {
      const res = await landingApi.UpdateSections(pageId, { page: page!.entity!, sections });
      if (res.page) {
        setPage(res.page);
        setSections(res.page.sections ?? []);
      }
    } catch (e) {
      setError(errMessage(e, t('landing.sections.saveError')));
    } finally {
      setSavingSections(false);
    }
  };

  const saveSettings = async (patch: Partial<LandingPage>) => {
    if (!pageId || !page) return;
    const res = await landingApi.UpdateLandingPage(pageId, { page: { ...page, ...patch } });
    if (res.page) setPage(res.page);
  };

  const publish = async () => {
    if (!pageId) return;
    try {
      const res = await landingApi.PublishLandingPage(pageId, {});
      if (res.page) setPage(res.page);
    } catch (e) {
      setError(errMessage(e, t('landing.pages.publishError')));
    }
  };

  const archive = async () => {
    if (!pageId) return;
    try {
      const res = await landingApi.ArchiveLandingPage(pageId, {});
      if (res.page) setPage(res.page);
    } catch (e) {
      setError(errMessage(e, t('landing.pages.archiveError')));
    }
  };

  const duplicate = async () => {
    if (!pageId) return;
    try {
      const res = await landingApi.DuplicateLandingPage(pageId, {});
      if (res.page?.entity?.id) navigate(`/landing/${res.page.entity.id}`);
    } catch (e) {
      setError(errMessage(e, t('landing.pages.duplicateError')));
    }
  };

  const handleDelete = async () => {
    if (!pageId) return;
    setDeleting(true);
    try {
      await landingApi.DeleteLandingPage(pageId);
      navigate('/landing');
    } catch (e) {
      setError(errMessage(e, t('landing.pages.deleteError')));
      setDeleting(false);
      setConfirmDelete(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4 p-6" aria-busy="true">
        <div className="h-8 w-64 animate-pulse rounded-md bg-muted" />
        <div className="h-64 animate-pulse rounded-md bg-muted" />
      </div>
    );
  }

  if (!page) {
    return (
      <div className="p-6">
        <Alert variant="error">{error ?? t('landing.pages.notFound')}</Alert>
        <Link to="/landing" className="mt-3 inline-block text-sm text-primary hover:underline">
          {t('landing.backToPages')}
        </Link>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      <div className="space-y-3 border-b border-border p-6">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <Link to="/landing" className="text-xs text-primary hover:underline">
              {t('landing.backToPages')}
            </Link>
            <div className="mt-1 flex items-center gap-2">
              <h1 className="text-xl font-semibold text-foreground">{page.title}</h1>
              <Badge variant={statusBadgeVariant(page.status)}>{t(`landing.status.${page.status}`)}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">/l/{page.slug}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <a href={`/l/${page.slug}`} target="_blank" rel="noreferrer">
              <Button size="sm" variant="secondary">
                {t('landing.pages.preview')}
              </Button>
            </a>
            {page.status !== 'LANDING_PAGE_STATUS_PUBLISHED' && (
              <Button size="sm" variant="secondary" onClick={publish}>
                {t('landing.pages.publish')}
              </Button>
            )}
            {page.status !== 'LANDING_PAGE_STATUS_ARCHIVED' && (
              <Button size="sm" variant="secondary" onClick={archive}>
                {t('landing.pages.archive')}
              </Button>
            )}
            <Button size="sm" variant="secondary" onClick={duplicate}>
              {t('common.duplicate')}
            </Button>
            <Button size="sm" variant="secondary" onClick={() => setTemplateOpen(true)}>
              {t('landing.templates.saveAsTemplate')}
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(true)}>
              {t('common.delete')}
            </Button>
          </div>
        </div>

        {error && <Alert variant="error">{error}</Alert>}

        <div role="tablist" aria-label={page.title} className="flex flex-wrap gap-1 border-b border-border">
          {TABS.map((v) => (
            <button
              key={v}
              role="tab"
              aria-selected={tab === v}
              onClick={() => setSearchParams({ tab: v })}
              className={`-mb-px border-b-2 px-3 py-2 text-sm ${
                tab === v ? 'border-primary font-medium text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {t(`landing.editor.tab.${v}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        {tab === 'content' && (
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-semibold text-foreground">{t('landing.sections.title')}</h2>
                <Button size="sm" onClick={saveSections} disabled={savingSections}>
                  {savingSections ? t('common.saving') : t('common.save')}
                </Button>
              </div>
              <SectionEditorList sections={sections} onChange={setSections} scope={scope} />
            </div>
            <div className="space-y-3">
              <h2 className="text-sm font-semibold text-foreground">{t('landing.sections.preview')}</h2>
              <div className="rounded-md border border-border" style={{ backgroundColor: page.theme?.background_color, color: page.theme?.text_color }}>
                {sections.length === 0 && <p className="p-8 text-center text-sm text-muted-foreground">{t('landing.sections.empty')}</p>}
                {sections.map((s) => (
                  <SectionRenderer key={s.id} section={s} />
                ))}
              </div>
            </div>
          </div>
        )}

        {tab === 'settings' && <PageSettingsForm page={page} onSave={saveSettings} />}

        {tab === 'analytics' && pageId && <PageAnalyticsPanel pageId={pageId} />}
      </div>

      <SaveAsTemplateDialog open={templateOpen} onClose={() => setTemplateOpen(false)} page={page} scope={scope} onSaved={() => setTemplateOpen(false)} />

      <ConfirmDialog
        open={confirmDelete}
        title={t('landing.pages.deleteConfirmTitle')}
        description={t('landing.pages.deleteConfirmBody', { title: page.title })}
        destructive
        isConfirming={deleting}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />
    </div>
  );
}
